import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import cx from 'classnames';
import { Button } from 'semantic-ui-react';
import { isEmpty } from 'lodash';

import { CSSTransition } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';

import { BodyClass, flattenToAppURL } from '@plone/volto/helpers';
import { Icon, LanguageSelector } from '@plone/volto/components';
import { isInternalURL } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import arrowRightSVG from '@plone/volto/icons/right-key.svg';
import arrowLeftSVG from '@plone/volto/icons/left-key.svg';
import zoomSVG from 'volto-dlr/icons/search-icon.svg';

const messages = defineMessages({
  closeMobileMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openMobileMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
});

const NavLink = ({ href, children, ...props }) => {
  if (isInternalURL(href) || href === '') {
    return (
      <Link to={href === '' ? '/' : href} {...props}>
        {children}
      </Link>
    );
  } else {
    return (
      <a
        href={href}
        rel="noopener noreferrer"
        target={config.settings.openExternalLinkInNewTab ? '_blank' : '_self'}
        {...props}
      >
        {children}
      </a>
    );
  }
};

const MobileNavigation = (props) => {
  const { pathname } = props;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [secondaryMenuOpened, setSecondaryMenuOpened] = React.useState(null);
  const [
    isSecondaryMobileMenuOpen,
    setIsSecondaryMobileMenuOpen,
  ] = React.useState(false);
  const [tertiaryMenuOpened, setTertiaryMenuOpened] = React.useState(null);
  const [
    isTertiaryMobileMenuOpen,
    setIsTertiaryMobileMenuOpen,
  ] = React.useState(false);
  const intl = useIntl();
  const menus = React.useRef(null);
  const currentLang = useSelector((state) => state.intl.locale);
  const iNavRootInfo = useSelector((state) => state.nearestInavRoot);
  const subsite = iNavRootInfo.subsite;
  const isSubsite = !isEmpty(subsite);
  const items = useSelector((state) => state.navigation.items || []);
  const history = useHistory();
  const isMonolingual =
    subsite?.['@id']?.startsWith('/de/schoollab') ||
    subsite?.['@id']?.startsWith('/de/next');

  const subsitePath = iNavRootInfo.subsite['@id'];
  const search = subsitePath
    ? {
        pathname: flattenToAppURL(subsitePath),
        query: '?allow_local=true',
      }
    : { pathname, query: '?allow_subsite=true' };
  const searchUrl =
    search.pathname.replace(/\/$/, '').replace(/\/$/, '') +
    '/@@search' +
    search.query;

  const Footer = () => (
    <ul className="mobile-tools">
      <li>
        {!isMonolingual && (
          <LanguageSelector onClickAction={closeMenus} fullLabel={true} />
        )}
      </li>
      <li>
        <Link
          className="accessibity-button"
          onClick={closeMenus}
          to={
            currentLang === 'de'
              ? '/de/service/barrierefreiheit'
              : '/en/service/accessibility'
          }
        >
          <FormattedMessage
            id="HeaderAccessibility"
            defaultMessage="Accessibility"
          />
        </Link>
      </li>
      <li>
        <Link
          className="presse-button"
          onClick={closeMenus}
          to={
            currentLang === 'de'
              ? '/de/aktuelles/presse'
              : '/en/latest/media-relations'
          }
        >
          <FormattedMessage id="Press" defaultMessage="Press" />
        </Link>
      </li>
      {isSubsite && (
        <li>
          <Link
            className="portal-button"
            onClick={closeMenus}
            to={`/${currentLang}`}
          >
            DLR Portal
          </Link>
        </li>
      )}
    </ul>
  );

  function toggleMobileMenu() {
    const body = document.getElementsByTagName('body')[0];
    if (!isMobileMenuOpen) {
      body.style.position = 'fixed';
      body.style.width = '100%';
      body.style.top = '0px';
      body.style.left = '0px';
    } else {
      body.style = '';
    }
    setIsMobileMenuOpen(!isMobileMenuOpen);

    if (isMobileMenuOpen) {
      setIsSecondaryMobileMenuOpen(false);
      setSecondaryMenuOpened(null);
      setIsTertiaryMobileMenuOpen(false);
      setTertiaryMenuOpened(null);
    }
  }

  function openSecondaryMenu(index) {
    setSecondaryMenuOpened(index);
    setIsSecondaryMobileMenuOpen(true);
  }

  function closeSecondaryMenu(e) {
    e.stopPropagation();
    setIsSecondaryMobileMenuOpen(false);
    setSecondaryMenuOpened(null);
  }

  function openTertiaryMenu(index) {
    setTertiaryMenuOpened(index);
    setIsTertiaryMobileMenuOpen(true);
  }

  function closeTertiaryMenu(e) {
    e.stopPropagation();
    setIsTertiaryMobileMenuOpen(false);
    setTertiaryMenuOpened(null);
  }

  function closeMenus(e) {
    e.stopPropagation();
    setIsSecondaryMobileMenuOpen(false);
    setIsTertiaryMobileMenuOpen(false);
    setSecondaryMenuOpened(null);
    setTertiaryMenuOpened(null);
    setIsMobileMenuOpen(false);
    const body = document.getElementsByTagName('body')[0];
    body.style = '';
  }

  return (
    <div className="mobile-nav mobile only tablet only" ref={menus}>
      <div className="hamburger-wrapper">
        <button
          className={cx('hamburger hamburger--collapse', {
            'is-active': isMobileMenuOpen,
          })}
          aria-label={
            isMobileMenuOpen
              ? intl.formatMessage(messages.closeMobileMenu)
              : intl.formatMessage(messages.openMobileMenu)
          }
          title={
            isMobileMenuOpen
              ? intl.formatMessage(messages.closeMobileMenu)
              : intl.formatMessage(messages.openMobileMenu)
          }
          type="button"
          onClick={toggleMobileMenu}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>
      </div>

      <CSSTransition
        in={isMobileMenuOpen}
        timeout={500}
        classNames="menu-drawer"
        unmountOnExit
      >
        <div className="menu-drawer">
          <BodyClass className="has-menu-open" />
          <div className="search-header">
            <Link
              to={searchUrl}
              title={intl.formatMessage(messages.search)}
              onClick={(e) => closeMenus(e)}
            >
              <Button
                basic
                icon
                aria-label={intl.formatMessage(messages.search)}
              >
                <Icon name={zoomSVG} size="36px" />
              </Button>
            </Link>
          </div>
          <ul className="sections">
            <li className="header">
              <Link
                to={isSubsite ? iNavRootInfo.subsite['@id'] : `/${currentLang}`}
                onClick={closeMenus}
              >
                <FormattedMessage id="Home" defaultMessage="Home" />
              </Link>
            </li>
            {items &&
              items.map((section, index) => (
                <li
                  key={section.url}
                  className={section.url === pathname ? 'current' : ''}
                  onClick={(e) => {
                    if (section.items.length > 0) {
                      openSecondaryMenu(index);
                    } else {
                      if (isInternalURL(section.url)) {
                        history.push(section.url);
                      } else {
                        e.preventDefault();
                        window.location.href = section.url;
                      }
                      return closeMenus(e);
                    }
                  }}
                  role="presentation"
                >
                  {section.nav_title || section.title}
                  {section.items.length > 0 && <Icon name={arrowRightSVG} />}
                  <CSSTransition
                    in={
                      isSecondaryMobileMenuOpen && secondaryMenuOpened === index
                    }
                    timeout={500}
                    classNames="menu-drawer"
                    unmountOnExit
                  >
                    <div className="menu-drawer subsection">
                      <div className="search-header">
                        <Link
                          to={searchUrl}
                          title={intl.formatMessage(messages.search)}
                          onClick={(e) => closeMenus(e)}
                        >
                          <Button
                            basic
                            icon
                            aria-label={intl.formatMessage(messages.search)}
                          >
                            <Icon name={zoomSVG} size="36px" />
                          </Button>
                        </Link>
                        <button onClick={(e) => closeSecondaryMenu(e)}>
                          <Icon name={arrowLeftSVG} size="60px" />
                          <span>
                            <FormattedMessage id="Back" defaultMessage="Back" />
                          </span>
                        </button>
                      </div>
                      <ul className="sections">
                        <li className="header">
                          {section.nav_title || section.title}
                        </li>
                        <li>
                          <NavLink
                            href={section.url === '' ? '/' : section.url}
                            onClick={(e) => closeMenus(e)}
                          >
                            <FormattedMessage
                              id="Overview"
                              defaultMessage="Overview"
                            />
                          </NavLink>
                        </li>

                        {section.items &&
                          section.items.map((subsection, index) => (
                            <li
                              key={subsection.url}
                              className={
                                subsection.url === pathname ? 'current' : ''
                              }
                              onClick={(e) => {
                                if (subsection.items.length > 0) {
                                  openTertiaryMenu(index);
                                } else {
                                  if (isInternalURL(subsection.url)) {
                                    history.push(subsection.url);
                                  } else {
                                    e.preventDefault();
                                    window.location.href = subsection.url;
                                  }
                                  return closeMenus(e);
                                }
                              }}
                              role="presentation"
                            >
                              {subsection.nav_title || subsection.title}
                              {subsection.items.length > 0 && (
                                <Icon name={arrowRightSVG} />
                              )}
                              <CSSTransition
                                in={
                                  isTertiaryMobileMenuOpen &&
                                  tertiaryMenuOpened === index
                                }
                                timeout={500}
                                classNames="menu-drawer"
                                unmountOnExit
                              >
                                <div className="menu-drawer subsection">
                                  <div className="search-header">
                                    <Link
                                      to={searchUrl}
                                      title={intl.formatMessage(
                                        messages.search,
                                      )}
                                      onClick={(e) => closeMenus(e)}
                                    >
                                      <Button
                                        basic
                                        icon
                                        aria-label={intl.formatMessage(
                                          messages.search,
                                        )}
                                      >
                                        <Icon name={zoomSVG} size="36px" />
                                      </Button>
                                    </Link>
                                    <button
                                      onClick={(e) => closeTertiaryMenu(e)}
                                    >
                                      <Icon name={arrowLeftSVG} size="60px" />
                                      <span>
                                        <FormattedMessage
                                          id="Back"
                                          defaultMessage="Back"
                                        />
                                      </span>
                                    </button>
                                  </div>
                                  <ul className="sections">
                                    <li className="header">
                                      {subsection.nav_title || subsection.title}
                                    </li>
                                    <li>
                                      <NavLink
                                        href={
                                          subsection.url === ''
                                            ? '/'
                                            : subsection.url
                                        }
                                        onClick={(e) => closeMenus(e)}
                                      >
                                        <FormattedMessage
                                          id="Overview"
                                          defaultMessage="Overview"
                                        />
                                      </NavLink>
                                    </li>
                                    {subsection.items &&
                                      subsection.items.map(
                                        (subsubsection, subindex) => (
                                          <li
                                            key={subsubsection.url}
                                            className={cx('sub-section', {
                                              'last-child':
                                                subindex ===
                                                subsection.items.length - 1,
                                            })}
                                          >
                                            <NavLink
                                              href={subsubsection.url}
                                              onClick={(e) => closeMenus(e)}
                                            >
                                              {subsubsection.nav_title ||
                                                subsubsection.title}
                                            </NavLink>
                                          </li>
                                        ),
                                      )}
                                  </ul>
                                  <Footer />
                                </div>
                              </CSSTransition>
                            </li>
                          ))}
                      </ul>
                      <Footer />
                    </div>
                  </CSSTransition>
                </li>
              ))}
          </ul>
          <Footer />
        </div>
      </CSSTransition>
    </div>
  );
};

export default MobileNavigation;
